import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Sidebar from '../../components/navigation/Sidebar';
import NotificationCenter from '../../components/navigation/NotificationCenter';
import QuickActionMenu from '../../components/navigation/QuickActionMenu';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import CaseCard from './components/CaseCard';
import ClientContractModal from './components/ClientContractModal';
import { useAuth } from '../../contexts/AuthContext';
import { getUserCases } from '../../services/caseService';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';
import {
  getUserContracts,
  completeContract,
  cancelContract,
  approveMilestone,
  approveTimeEntry,
  releaseEscrow,
} from '../../services/contractService';

const STATUS_MAP = {
  draft: 'pending',
  intake_submitted: 'in-progress',
  needs_clarification: 'clarification-needed',
  report_generating: 'in-progress',
  report_ready: 'completed',
  archived: 'completed',
};

const PERSONA_LABELS = {
  'insurance-adjuster': 'Insurance Adjuster',
  'plaintiff-attorney': 'Plaintiff Attorney',
  'defense-attorney': 'Defense Attorney',
  'siu-investigator': 'SIU Investigator',
  'subrogation-specialist': 'Subrogation Specialist',
  prosecutor: 'Prosecutor',
  'fire-marshal': 'Fire Marshal/AHJ',
  'risk-manager': 'Risk Manager',
  'property-owner': 'Property Owner/Insured',
};

const CONTRACT_STATUS_COLORS = {
  offered:        'bg-info/10 text-info border-info/30',
  accepted:       'bg-success/10 text-success border-success/30',
  in_progress:    'bg-primary/10 text-primary border-primary/30',
  pending_review: 'bg-warning/10 text-warning border-warning/30',
  completed:      'bg-success/10 text-success border-success/30',
  cancelled:      'bg-destructive/10 text-destructive border-destructive/30',
};
const CONTRACT_STATUS_LABELS = {
  offered:        'New Offer',
  accepted:       'Accepted',
  in_progress:    'In Progress',
  pending_review: 'Pending Review',
  completed:      'Completed',
  cancelled:      'Cancelled',
  disputed:       'Disputed',
};

const ClientActivity = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, signOut } = useAuth();

  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') || 'cases');
  const [contractTab, setContractTab] = useState('all');
  const [cases, setCases] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [reports, setReports] = useState([]);
  const [detailContract, setDetailContract] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [reportSearch, setReportSearch] = useState('');

  useEffect(() => {
    const t = searchParams.get('tab');
    if (t) setActiveTab(t);
  }, [searchParams]);

  useEffect(() => {
    if (user) loadData();
  }, [user?.id]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [casesResult, contractsResult, reportsResult] = await Promise.all([
        getUserCases(user?.id),
        getUserContracts(user?.id, 'client'),
        supabase
          .from('cases')
          .select(`id, case_number, persona, incident_date, property_address, property_type,
            allegation_type, claim_number, triage_confidence, triage_summary,
            report_generated_at, created_at, reports(id, generated_at, pdf_url)`)
          .eq('status', 'report_ready')
          .eq('user_id', user?.id)
          .order('report_generated_at', { ascending: false }),
      ]);
      if (casesResult?.success) setCases(casesResult?.data || []);
      if (contractsResult?.success) setContracts(contractsResult?.contracts || []);
      if (!reportsResult?.error) setReports(reportsResult?.data || []);
    } catch (error) {
      console.error('Load activity error:', error);
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Case formatting ─────────────────────────────────────────────────────────
  const filteredCases = cases?.filter((c) => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (
      c?.case_number?.toLowerCase()?.includes(q) ||
      c?.property_address?.toLowerCase()?.includes(q) ||
      c?.allegation_type?.toLowerCase()?.includes(q)
    );
  });

  const formattedCases = filteredCases?.map((c) => ({
    caseNumber: c?.case_number,
    caseName: `${c?.allegation_type?.replace('-', ' ')} - ${c?.property_address}`,
    submissionDate: new Date(c?.created_at)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }),
    status: STATUS_MAP[c?.status] || 'pending',
    persona: PERSONA_LABELS[c?.persona] || c?.persona,
    expertAssigned: null,
    progress:
      c?.status === 'report_ready'
        ? 100
        : c?.status === 'report_generating'
          ? 75
          : c?.status === 'needs_clarification'
            ? 50
            : 25,
    caseId: c?.id,
  }));

  // ── Contract helpers ────────────────────────────────────────────────────────
  const pendingActionsTotal = contracts.reduce(
    (s, c) =>
      s +
      (c.contract_milestones || []).filter((m) => m.status === 'submitted').length +
      (c.time_entries || []).filter((e) => e.status === 'pending').length,
    0,
  );

  const CONTRACT_SUB_TABS = [
    { id: 'all',       label: 'All',           statuses: null },
    { id: 'new',       label: 'New Offers',    statuses: ['offered'] },
    { id: 'active',    label: 'Active',        statuses: ['accepted', 'in_progress'] },
    { id: 'review',    label: 'Review Needed', statuses: ['pending_review'] },
    { id: 'completed', label: 'Completed',     statuses: ['completed'] },
    { id: 'cancelled', label: 'Cancelled',     statuses: ['cancelled'] },
    { id: 'disputed',  label: 'Disputed',      statuses: ['disputed'] },
  ];

  const filteredContracts = contractTab === 'all'
    ? contracts
    : contracts.filter((c) => {
        const tab = CONTRACT_SUB_TABS.find((t) => t.id === contractTab);
        return tab?.statuses?.includes(c.status);
      });

  // ── Report helpers ──────────────────────────────────────────────────────────
  const REPORT_PERSONA_LABELS = {
    'insurance-adjuster': 'Insurance Adjuster',
    'plaintiff-attorney': 'Plaintiff Attorney',
    'defense-attorney': 'Defense Attorney',
    'siu-investigator': 'SIU Investigator',
    'subrogation-specialist': 'Subrogation Specialist',
    prosecutor: 'Prosecutor',
    'fire-marshal': 'Fire Marshal / AHJ',
    'risk-manager': 'Risk Manager',
    'property-owner': 'Property Owner',
  };
  const ALLEGATION_LABELS = {
    arson: 'Arson',
    'electrical-fire': 'Electrical Fire',
    'suspicious-fire': 'Suspicious Fire',
    'accidental-fire': 'Accidental Fire',
    wildfire: 'Wildfire',
    explosion: 'Explosion',
  };
  const filteredReports = reports.filter((r) => {
    if (!reportSearch) return true;
    const q = reportSearch.toLowerCase();
    return (
      r.case_number?.toLowerCase().includes(q) ||
      r.property_address?.toLowerCase().includes(q) ||
      r.allegation_type?.toLowerCase().includes(q)
    );
  });
  const confidenceBadge = (score) => {
    if (score == null) return null;
    const pct = Math.round(score * 100);
    const color =
      pct >= 80 ? 'text-success bg-success/10' :
      pct >= 60 ? 'text-warning bg-warning/10' :
                  'text-destructive bg-destructive/10';
    return { pct, color };
  };

  // ── Tabs config ─────────────────────────────────────────────────────────────
  const tabs = [
    { id: 'cases', label: 'My Cases', icon: 'FileText' },
    {
      id: 'contracts',
      label: 'Contracts',
      icon: 'FileSignature',
      badge: pendingActionsTotal > 0 ? pendingActionsTotal : null,
    },
    { id: 'reports', label: 'Reports', icon: 'BookOpen' },
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Icon name="Loader" size={48} className="text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar userRole="client" />
      <QuickActionMenu userRole="client" />
      <div className="lg:ml-60 min-h-screen">
        {/* ── Header ── */}
        <header className="sticky top-0 z-40 bg-card border-b border-border px-4 md:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => {
                  const c = document.body.classList.toggle('sidebar-collapsed');
                  localStorage.setItem('sidebarCollapsed', c.toString());
                }}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
                aria-label="Toggle sidebar"
              >
                <Icon name="PanelLeft" size={24} />
              </button>
              <div>
                <h1 className="text-2xl md:text-3xl font-semibold text-foreground">Activity</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Manage your cases, contracts &amp; reports in one place.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <NotificationCenter userRole="client" />
              <Button
                variant="default"
                iconName="FilePlus"
                iconPosition="left"
                onClick={() => navigate('/new-case-creation-wizard')}
                className="hidden md:flex"
              >
                New Case
              </Button>
              <Button
                variant="outline"
                iconName="LogOut"
                iconPosition="left"
                onClick={async () => {
                  await signOut();
                  navigate('/login');
                }}
              >
                Logout
              </Button>
            </div>
          </div>
        </header>

        <main className="px-4 md:px-6 lg:px-8 py-6 md:py-8 space-y-6">
          {/* ── Tab bar ── */}
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="border-b border-border">
              <div className="flex">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-smooth border-b-2 ${
                      activeTab === tab.id
                        ? 'border-primary text-primary bg-primary/5'
                        : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }`}
                  >
                    <Icon name={tab.icon} size={15} />
                    {tab.label}
                    {tab.badge && (
                      <span className="px-1.5 py-0.5 text-xs rounded-full bg-warning text-white font-bold">
                        {tab.badge}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="p-6">
              {/* ════════════ MY CASES ════════════ */}
              {activeTab === 'cases' && (
                <>
                  {/* Search bar */}
                  <div className="mb-5">
                    <div className="relative max-w-md">
                      <Icon
                        name="Search"
                        size={16}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                      />
                      <input
                        type="text"
                        placeholder="Search cases..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 text-sm bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {formattedCases?.map((caseItem) => (
                      <CaseCard key={caseItem?.caseId} caseData={caseItem} />
                    ))}
                    {(!formattedCases || formattedCases.length === 0) && (
                      <div className="col-span-full text-center py-12">
                        <Icon
                          name="FileText"
                          size={40}
                          className="text-muted-foreground mx-auto mb-3 opacity-40"
                        />
                        <p className="text-muted-foreground text-sm">
                          No cases yet. Create your first case to get started.
                        </p>
                        <button
                          className="mt-4 text-sm text-primary hover:underline"
                          onClick={() => navigate('/new-case-creation-wizard')}
                        >
                          Create New Case
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* ════════════ CONTRACTS ════════════ */}
              {activeTab === 'contracts' && (
                <>
                  {contracts.length === 0 ? (
                    <div className="text-center py-12">
                      <Icon
                        name="FileSignature"
                        size={40}
                        className="text-muted-foreground mx-auto mb-3 opacity-40"
                      />
                      <p className="text-muted-foreground text-sm">
                        No contracts yet. Hire an expert to get started.
                      </p>
                      <button
                        className="mt-4 text-sm text-primary hover:underline"
                        onClick={() => navigate('/expert-marketplace-listing')}
                      >
                        Browse Experts
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Summary metrics */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-2">
                        {[
                          {
                            label: 'Total',
                            value: contracts.length,
                            icon: 'FileText',
                            color: 'text-foreground',
                            bg: 'bg-muted',
                            onClick: () => setContractTab('all'),
                          },
                          {
                            label: 'Active',
                            value: contracts.filter((c) =>
                              ['accepted', 'in_progress'].includes(c.status),
                            ).length,
                            icon: 'Zap',
                            color: 'text-primary',
                            bg: 'bg-primary/10',
                            onClick: () => setContractTab('active'),
                          },
                          {
                            label: 'Completed',
                            value: contracts.filter((c) => c.status === 'completed').length,
                            icon: 'CheckCircle',
                            color: 'text-success',
                            bg: 'bg-success/10',
                            onClick: () => setContractTab('completed'),
                          },
                          {
                            label: 'Actions Needed',
                            value: pendingActionsTotal,
                            icon: 'Bell',
                            color: 'text-warning',
                            bg: 'bg-warning/10',
                            onClick: () => setContractTab('active'),
                          },
                        ].map((s) => (
                          <button
                            key={s.label}
                            onClick={s.onClick}
                            className="flex items-center gap-3 p-3 bg-background border border-border rounded-xl hover:border-primary/40 transition-colors text-left"
                          >
                            <div
                              className={`w-9 h-9 rounded-lg ${s.bg} flex items-center justify-center flex-shrink-0`}
                            >
                              <Icon name={s.icon} size={16} className={s.color} />
                            </div>
                            <div>
                              <p className={`text-lg font-bold ${s.color}`}>{s.value}</p>
                              <p className="text-xs text-muted-foreground">{s.label}</p>
                            </div>
                          </button>
                        ))}
                      </div>

                      {/* Contract sub-tabs */}
                      <div className="flex flex-wrap gap-1.5">
                        {CONTRACT_SUB_TABS.map((tab) => {
                          const count = tab.statuses
                            ? contracts.filter((c) => tab.statuses.includes(c.status)).length
                            : contracts.length;
                          if (tab.id !== 'all' && count === 0) return null;
                          return (
                            <button
                              key={tab.id}
                              onClick={() => setContractTab(tab.id)}
                              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${
                                contractTab === tab.id
                                  ? 'bg-primary text-primary-foreground border-primary'
                                  : 'bg-background text-muted-foreground border-border hover:text-foreground hover:border-primary/40'
                              }`}
                            >
                              {tab.label}
                              <span
                                className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                                  contractTab === tab.id
                                    ? 'bg-primary-foreground/20 text-primary-foreground'
                                    : 'bg-muted text-muted-foreground'
                                }`}
                              >
                                {count}
                              </span>
                            </button>
                          );
                        })}
                      </div>

                      {/* Contract cards */}
                      {filteredContracts.length === 0 ? (
                        <div className="text-center py-10">
                          <Icon
                            name="FileSearch"
                            size={36}
                            className="text-muted-foreground mx-auto mb-3 opacity-40"
                          />
                          <p className="text-muted-foreground text-sm">
                            No contracts in this category.
                          </p>
                        </div>
                      ) : (
                        filteredContracts.map((contract) => (
                          <ContractCard
                            key={contract.id}
                            contract={contract}
                            onViewDetails={() => setDetailContract(contract)}
                            onRefresh={loadData}
                          />
                        ))
                      )}
                    </div>
                  )}
                </>
              )}

              {/* ════════════ REPORTS ════════════ */}
              {activeTab === 'reports' && (
                <>
                  {/* Search bar */}
                  <div className="mb-5">
                    <div className="relative max-w-md">
                      <Icon
                        name="Search"
                        size={16}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                      />
                      <input
                        type="text"
                        placeholder="Search by case number, address or type…"
                        value={reportSearch}
                        onChange={(e) => setReportSearch(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 text-sm bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                    </div>
                  </div>

                  {filteredReports.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                      <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mb-4">
                        <Icon name="BookOpen" size={24} className="text-muted-foreground" />
                      </div>
                      <h3 className="text-base font-semibold text-foreground mb-1">
                        {reportSearch ? 'No reports match your search' : 'No completed reports yet'}
                      </h3>
                      <p className="text-sm text-muted-foreground max-w-sm mb-5">
                        {reportSearch
                          ? 'Try a different search term.'
                          : 'Once a case is analysed, the completed report will appear here.'}
                      </p>
                      {!reportSearch && (
                        <Button
                          variant="default"
                          size="sm"
                          iconName="FilePlus"
                          iconPosition="left"
                          onClick={() => navigate('/new-case-creation-wizard')}
                        >
                          Create a Case
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                      {filteredReports.map((item) => {
                        const report = item.reports?.[0];
                        const badge = confidenceBadge(item.triage_confidence);
                        const generatedDate = report?.generated_at || item.report_generated_at;
                        return (
                          <div
                            key={item.id}
                            className="bg-background border border-border rounded-xl p-5 flex flex-col gap-4 hover:border-primary/40 transition-colors group"
                          >
                            {/* Top row */}
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex items-center gap-2">
                                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                                  <Icon name="FileText" size={17} className="text-primary" />
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground">Case</p>
                                  <p className="text-sm font-semibold text-foreground">
                                    #{item.case_number}
                                  </p>
                                </div>
                              </div>
                              {badge && (
                                <span
                                  className={`text-xs font-semibold px-2.5 py-1 rounded-full flex-shrink-0 ${badge.color}`}
                                >
                                  {badge.pct}% confidence
                                </span>
                              )}
                            </div>

                            {/* Address + type */}
                            <div>
                              <p className="text-sm font-medium text-foreground line-clamp-1">
                                {item.property_address}
                              </p>
                              <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1">
                                <span className="text-xs text-muted-foreground capitalize">
                                  {ALLEGATION_LABELS[item.allegation_type] || item.allegation_type}
                                </span>
                                <span className="text-xs text-muted-foreground">·</span>
                                <span className="text-xs text-muted-foreground capitalize">
                                  {item.property_type}
                                </span>
                              </div>
                            </div>

                            {/* Meta grid */}
                            <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                              <div className="flex items-center gap-1.5">
                                <Icon name="Calendar" size={12} />
                                {item.incident_date
                                  ? new Date(item.incident_date).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric',
                                    })
                                  : '—'}
                              </div>
                              <div className="flex items-center gap-1.5 text-success">
                                <Icon name="CheckCircle" size={12} />
                                {generatedDate
                                  ? new Date(generatedDate).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric',
                                    })
                                  : 'Ready'}
                              </div>
                              <div className="flex items-center gap-1.5 col-span-2">
                                <Icon name="User" size={12} />
                                {REPORT_PERSONA_LABELS[item.persona] || item.persona}
                              </div>
                              {item.claim_number && (
                                <div className="flex items-center gap-1.5 col-span-2">
                                  <Icon name="Hash" size={12} />
                                  Claim {item.claim_number}
                                </div>
                              )}
                            </div>

                            {/* Triage summary */}
                            {item.triage_summary && (
                              <p className="text-xs text-muted-foreground line-clamp-2 border-t border-border pt-3">
                                {item.triage_summary}
                              </p>
                            )}

                            {/* Actions */}
                            <div className="flex gap-2 mt-auto pt-2 border-t border-border">
                              <Button
                                variant="default"
                                size="sm"
                                iconName="ExternalLink"
                                iconPosition="left"
                                className="flex-1"
                                onClick={() => navigate(`/report-viewer/${item.id}`)}
                              >
                                View Report
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

            </div>
          </div>
        </main>
      </div>

      {/* Contract detail modal */}
      {detailContract && (
        <ClientContractModal
          contract={detailContract}
          onClose={() => setDetailContract(null)}
          onRefresh={loadData}
        />
      )}
    </div>
  );
};

// ── Contract card sub-component ───────────────────────────────────────────────
const ContractCard = ({ contract, onViewDetails, onRefresh }) => {
  const submittedMilestones = (contract.contract_milestones || []).filter(
    (m) => m.status === 'submitted',
  );
  const pending = submittedMilestones.length;
  const isActive = !['completed', 'cancelled', 'disputed'].includes(contract.status);
  const [quickLoading, setQuickLoading] = useState(null);

  const handleQuickApproveAll = async (e) => {
    e.stopPropagation();
    if (quickLoading) return;
    setQuickLoading('approve');
    try {
      const promises = [
        ...submittedMilestones.map((m) => approveMilestone(m.id)),
      ];
      const results = await Promise.allSettled(promises);
      const failed = results.filter(r => r.status === 'rejected').length;
      if (failed > 0) {
        toast.error(`${failed} of ${promises.length} approval(s) failed`);
      } else {
        toast.success(`Approved ${promises.length} item${promises.length > 1 ? 's' : ''}`);
      }
      onRefresh?.();
    } catch {
      toast.error('Some approvals failed');
    } finally {
      setQuickLoading(null);
    }
  };

  const handleQuickComplete = async (e) => {
    e.stopPropagation();
    if (quickLoading) return;

    const isHourly          = contract?.contract_type === 'hourly';
    const isFixedOrMilestone = contract?.contract_type === 'fixed' || contract?.contract_type === 'milestone';



    if (
      !window.confirm(
        'Mark this contract as complete? This will release escrow funds to the expert.',
      )
    )
      return;

    // Determine the approved amount
    let approvedAmount;
    if (contract?.escrow_status === 'held') {
      const cap = contract?.escrow_amount ?? contract?.budget_cap;

      if (isHourly) {
        // approvedBill = sum of approved time entries
        const approvedBill = (contract.time_entries || [])
          .filter(e => e.status === 'approved')
          .reduce((s, e) => s + (e.billable_amount || 0), 0);
        const raw = window.prompt(
          `Approved time total: $${approvedBill.toFixed(2)}\nEscrowed (budget cap): $${cap ? cap.toFixed(2) : '?'}\n\nEnter the amount to release (≥ $${approvedBill.toFixed(2)}):`,
          approvedBill.toFixed(2)
        );
        if (raw === null) return;
        const parsed = parseFloat(raw);
        if (isNaN(parsed) || parsed <= 0) {
          toast.error('Invalid amount — must be greater than $0');
          return;
        }
        if (parsed < approvedBill) {
          toast.error(`Amount must be at least the approved time total ($${approvedBill.toFixed(2)})`);
          return;
        }
        if (cap && parsed > cap) {
          toast.error(`Amount cannot exceed the escrowed amount ($${cap.toFixed(2)})`);
          return;
        }
        approvedAmount = parsed;
      } else if (isFixedOrMilestone) {
        const raw = window.prompt(
          `How much should be released to the expert? (Escrowed: $${cap ? cap.toFixed(2) : '?'})\nLeave blank to release the full amount.`,
          cap != null ? cap.toFixed(2) : ''
        );
        if (raw === null) return;
        if (raw.trim() !== '') {
          const parsed = parseFloat(raw);
          if (isNaN(parsed) || parsed <= 0) {
            toast.error('Invalid amount — must be greater than $0');
            return;
          }
          if (cap && parsed > cap) {
            toast.error(`Amount cannot exceed the escrowed amount ($${cap.toFixed(2)})`);
            return;
          }
          approvedAmount = parsed;
        }
      }
    }

    setQuickLoading('complete');
    try {
      const r = await completeContract(contract.id, approvedAmount);
      if (r?.success) {
        toast.success('Contract completed');
        onRefresh?.();
      } else toast.error(r?.error || 'Failed to complete contract');
    } finally {
      setQuickLoading(null);
    }
  };

  const handleQuickCancel = async (e) => {
    e.stopPropagation();
    if (quickLoading) return;
    const reason = window.prompt('Reason for cancellation (required):');
    if (!reason?.trim()) {
      if (reason !== null) toast.error('Cancellation reason is required');
      return;
    }
    setQuickLoading('cancel');
    try {
      const hadEscrow = contract?.escrow_status === 'held';
      const r = await cancelContract(contract.id, reason.trim());
      if (r?.success) {
        toast.success(hadEscrow ? 'Contract cancelled — escrow released to the expert in full' : 'Contract cancelled');
        onRefresh?.();
      } else toast.error(r?.error || 'Failed to cancel contract');
    } finally {
      setQuickLoading(null);
    }
  };

  const handleQuickRelease = async (e) => {
    e.stopPropagation();
    if (quickLoading) return;
    if (!window.confirm('Release escrow funds to the expert?')) return;

    // For fixed/milestone contracts, optionally ask how much work was approved.
    let approvedAmount;
    const isFixedOrMilestone =
      contract?.contract_type === 'fixed' || contract?.contract_type === 'milestone';
    if (isFixedOrMilestone && contract?.escrow_status === 'held') {
      const cap = contract?.escrow_amount ?? contract?.budget_cap;
      const raw = window.prompt(
        `How much should be released to the expert? (Budget cap: $${cap ? cap.toFixed(2) : '?'})\nLeave blank to release the full amount.`,
        cap != null ? cap.toFixed(2) : ''
      );
      if (raw === null) return;
      if (raw.trim() !== '') {
        const parsed = parseFloat(raw);
        if (isNaN(parsed) || parsed <= 0) {
          toast.error('Invalid amount — must be greater than $0');
          return;
        }
        if (cap && parsed > cap) {
          toast.error(`Amount cannot exceed the budget cap ($${cap.toFixed(2)})`);
          return;
        }
        approvedAmount = parsed;
      }
    }

    setQuickLoading('release');
    try {
      const r = await releaseEscrow(contract.id, approvedAmount);
      if (r?.success) {
        toast.success('Escrow released');
        onRefresh?.();
      } else toast.error(r?.error || 'Failed to release escrow');
    } finally {
      setQuickLoading(null);
    }
  };

  return (
    <div className="bg-background border border-border rounded-xl hover:border-primary/40 transition-colors overflow-hidden">
      {/* Main row */}
      <div className="flex items-center justify-between p-4">
        <div className="flex-1 min-w-0 mr-4">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <p className="font-semibold text-foreground truncate">{contract.title}</p>
            <span
              className={`px-2 py-0.5 text-xs rounded border font-medium flex-shrink-0 ${CONTRACT_STATUS_COLORS[contract.status] || 'bg-muted text-muted-foreground border-border'}`}
            >
              {CONTRACT_STATUS_LABELS[contract.status] || contract.status}
            </span>
            {pending > 0 && (
              <span className="flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-warning text-white font-bold flex-shrink-0">
                <Icon name="Bell" size={10} />
                {pending} action{pending > 1 ? 's' : ''}
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span>#{contract.contract_number}</span>
            <span className="flex items-center gap-1">
              <Icon
                name={contract.contract_type === 'hourly' ? 'Clock' : 'CheckSquare'}
                size={11}
              />
              {contract.contract_type === 'hourly' ? 'Hourly' : 'Milestone'}
            </span>
            {contract.expert_profiles && (
              <span className="flex items-center gap-1">
                <Icon name="UserCheck" size={11} />
                {contract.expert_profiles.full_name || 'Expert'}
              </span>
            )}
            {contract.contract_type === 'hourly' && contract.hourly_rate && (
              <span className="font-medium text-foreground">${contract.hourly_rate}/hr</span>
            )}
            {contract.contract_type === 'milestone' && contract.total_contract_value && (
              <span className="font-medium text-foreground">
                ${contract.total_contract_value?.toFixed(2)}
              </span>
            )}
            {contract.deadline && (
              <span className="flex items-center gap-1">
                <Icon name="Calendar" size={11} />
                {new Date(contract.deadline).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                })}
              </span>
            )}
          </div>
        </div>
        <Button size="sm" onClick={onViewDetails} iconName="ExternalLink" iconPosition="left">
          Details
        </Button>
      </div>

      {/* Quick actions bar */}
      {isActive && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-muted/30 border-t border-border overflow-x-auto">
          {pending > 0 && (
            <button
              onClick={handleQuickApproveAll}
              disabled={!!quickLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-success/10 text-success hover:bg-success/20 border border-success/20 transition-colors whitespace-nowrap disabled:opacity-50 disabled:pointer-events-none"
            >
              <Icon name="CheckCircle" size={13} />
              Approve All ({pending})
            </button>
          )}
          {contract.status === 'pending_review' && (
            <button
              onClick={onViewDetails}
              disabled={!!quickLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-warning/10 text-warning hover:bg-warning/20 border border-warning/20 transition-colors whitespace-nowrap disabled:opacity-50 disabled:pointer-events-none"
            >
              <Icon name="Trophy" size={13} />
              Approve Work
            </button>
          )}
          {contract.escrow_status === 'held' && contract.status === 'completed' && (
            <button
              onClick={handleQuickRelease}
              disabled={!!quickLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-success/10 text-success hover:bg-success/20 border border-success/20 transition-colors whitespace-nowrap disabled:opacity-50 disabled:pointer-events-none"
            >
              <Icon name="Unlock" size={13} />
              Release Payment
            </button>
          )}
          <button
            onClick={onViewDetails}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80 border border-border transition-colors whitespace-nowrap"
          >
            <Icon name="Eye" size={13} />
            Review
          </button>
          <button
            onClick={handleQuickCancel}
            disabled={!!quickLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg text-destructive hover:bg-destructive/10 border border-transparent hover:border-destructive/20 transition-colors whitespace-nowrap ml-auto disabled:opacity-50 disabled:pointer-events-none"
          >
            <Icon name="XCircle" size={13} />
            Stop
          </button>
        </div>
      )}
    </div>
  );
};

export default ClientActivity;
